import { useEffect } from 'react';
import { ComparisonBar, type ComparisonBarItem } from '@highereducation/hen-ui';
import { useComparison } from '../../providers/ComparisonProvider';
import { useResultsPageConfig } from '../../providers/ResultsPageConfigProvider';
import { useTracking } from '../../providers/TrackingProvider';
import { uuid } from '../../shared/utils';

const CLEAR_SELECTION_LABEL = 'Clear Selection';
const SEE_COMPARISON_LABEL = 'See Comparison';
const REMOVE_LABEL = 'X';

const COMPARISON_BAR_LOCATION = 'dexp-comparison-bar';
const COMPARISON_BAR_ELEMENT = 'dexp-comparison-bar';
const CLEAR_SELECTION_ELEMENT = 'dexp-comparison-clear-selection';
const SEE_COMPARISON_ELEMENT = 'dexp-comparison-see-comparison';

function formatBannerText(
  template: string | undefined,
  count: number,
): string | undefined {
  return template?.replace(/\{count\}/g, String(count));
}

const ComparisonBanner = () => {
  const {
    programsToCompare,
    isBannerVisible,
    minItems,
    maxItems,
    toggleComparisonModal,
    clearProgramsToCompare,
    removeProgramFromCompare,
    dslConfigId,
  } = useComparison();
  const { getComparisonCopy } = useResultsPageConfig();
  const { bannerText, ctaText } = getComparisonCopy();

  // Tolerant read: returns `undefined` outside a `TrackingProvider`
  // (legacy render paths / unit tests) so the banner clears without
  // crashing when there is no flow tracking context to beam into.
  const tracking = useTracking();

  // On the DSL result page the surrounding `ComparisonProvider` carries
  // the published `dslConfigId`; thread it onto every comparison-bar beam
  // so the bar's view / clear / compare / remove events correlate to the
  // config, at parity with the compare-checkbox and modal beams. Omitted
  // on the imperative path, leaving those payloads unchanged.
  const dslConfigDimensions = dslConfigId
    ? { customDimensions: [{ key: 'dslConfigId', value: dslConfigId }] }
    : undefined;

  const isVisible = isBannerVisible && programsToCompare.length > 0;

  // When the bar first becomes visible it emits a passive `ElementViewed`
  // beam for the bar itself and each of its CTAs (clear / see comparison)
  // on the inherited correlation chain — fresh per-element `correlationId`,
  // shared `viewCorrelationId` off the event context. Keyed on `isVisible`
  // so it re-fires each time the bar reappears after being cleared, and
  // no-ops outside a `TrackingProvider`.
  useEffect(() => {
    if (!isVisible) return;

    const viewCorrelationId = tracking?.eventContext?.viewCorrelationId;

    tracking?.triggers.triggerElementViewedEvent?.({
      correlationId: uuid(),
      viewCorrelationId,
      elementType: COMPARISON_BAR_ELEMENT,
      name: COMPARISON_BAR_ELEMENT,
      location: COMPARISON_BAR_LOCATION,
      ...dslConfigDimensions,
    });
    tracking?.triggers.triggerElementViewedEvent?.({
      correlationId: uuid(),
      viewCorrelationId,
      elementType: CLEAR_SELECTION_ELEMENT,
      name: CLEAR_SELECTION_ELEMENT,
      text: CLEAR_SELECTION_LABEL,
      location: COMPARISON_BAR_LOCATION,
      ...dslConfigDimensions,
    });
    tracking?.triggers.triggerElementViewedEvent?.({
      correlationId: uuid(),
      viewCorrelationId,
      elementType: SEE_COMPARISON_ELEMENT,
      name: SEE_COMPARISON_ELEMENT,
      text: SEE_COMPARISON_LABEL,
      location: COMPARISON_BAR_LOCATION,
      ...dslConfigDimensions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, dslConfigId]);

  if (!isVisible) {
    return null;
  }

  // Removing a single program from the bar is its own discrete user
  // interaction (the X on a school chip), so it emits an `ElementClicked`
  // beam on the same correlation chain as the surrounding flow (inherited
  // `viewCorrelationId`, fresh per-click `correlationId`) — parity with
  // the clear / compare beams and the modal's remove beam.
  const handleRemove = (programId: number) => {
    removeProgramFromCompare(programId);
    tracking?.triggers.triggerElementClickedEvent({
      correlationId: uuid(),
      viewCorrelationId: tracking.eventContext?.viewCorrelationId,
      elementType: 'dexp-comparison-banner',
      name: 'dexp-comparison-banner-remove-button',
      text: REMOVE_LABEL,
      location: 'dexp-comparison-bar',
      ...dslConfigDimensions,
    });
  };

  const items: ComparisonBarItem[] = programsToCompare.map((program) => ({
    id: String(program.programId),
    schoolLogo: program.imageURL ?? '',
    schoolName: program.schoolName,
    programName: program.programName,
    onRemove: () => handleRemove(program.programId),
  }));

  // Clearing the comparison set is a discrete user interaction, so it
  // emits its own `ElementClicked` beam on the same correlation chain
  // as the surrounding flow (`viewCorrelationId` off the inherited
  // event context, a fresh per-click `correlationId`).
  const handleClear = () => {
    clearProgramsToCompare();
    tracking?.triggers.triggerElementClickedEvent({
      correlationId: uuid(),
      viewCorrelationId: tracking.eventContext?.viewCorrelationId,
      elementType: 'dexp-comparison-banner',
      name: 'dexp-comparison-banner-clear-selection-button',
      text: CLEAR_SELECTION_LABEL,
      location: 'dexp-comparison-bar',
      ...dslConfigDimensions,
    });
  };

  // Opening the comparison modal is its own discrete user interaction, so
  // it emits an `ElementClicked` beam on the same correlation chain as the
  // surrounding flow (inherited `viewCorrelationId`, fresh per-click
  // `correlationId`).
  const handleCompare = () => {
    toggleComparisonModal(true);
    tracking?.triggers.triggerElementClickedEvent({
      correlationId: uuid(),
      viewCorrelationId: tracking.eventContext?.viewCorrelationId,
      elementType: 'dexp-comparison-banner',
      name: 'dexp-comparison-banner-see-comparison-button',
      text: SEE_COMPARISON_LABEL,
      location: 'dexp-comparison-bar',
      ...dslConfigDimensions,
    });
  };

  return (
    <ComparisonBar
      items={items}
      maxItems={maxItems}
      minItemsToCompare={minItems}
      statusText={formatBannerText(bannerText, programsToCompare.length)}
      compareLabel={ctaText ?? SEE_COMPARISON_LABEL}
      clearLabel={CLEAR_SELECTION_LABEL}
      onCompare={handleCompare}
      onClear={handleClear}
      reserveViewportSpace
    />
  );
};

export default ComparisonBanner;
